import { createSignal, onCleanup, Show } from 'solid-js'
import ConsentBanner from './ConsentBanner'
import ConsentPreferencesModal from './ConsentPreferencesModal'
import { COPY } from './copy'
import { getConfig } from './config'
import { getConsent, isGpcEnabled, optOutOfSale, saveConsent, type ConsentSurface, type StoredConsent } from './consent-store'

export interface ConsentRootProps {
  root: Document | ShadowRoot
  initiallyOpen: boolean
  onSave?: (s: StoredConsent) => unknown
  /** receives open(source) so window.TuneConsent.open() can reopen the modal */
  registerOpen: (open: (source?: ConsentSurface, trigger?: Element | null) => void) => void
  /** receives doNotSell(source) so window.TuneConsent.doNotSell() (footer CCPA link) shares the notices */
  registerDoNotSell?: (doNotSell: (source?: ConsentSurface) => boolean) => void
}

/** Orchestrates banner -> modal -> saved notice. */
export default function ConsentRoot(props: ConsentRootProps) {
  const gpc = isGpcEnabled()
  const [banner, setBanner] = createSignal(props.initiallyOpen)
  const [modal, setModal] = createSignal(false)
  const [modalSource, setModalSource] = createSignal<ConsentSurface>('preferences_modal')
  const [busy, setBusy] = createSignal(false)
  const [notice, setNotice] = createSignal(false)
  const [saveError, setSaveError] = createSignal(false)
  let returnFocus: Element | null = null
  let timer: any

  const openModal = (source: ConsentSurface, trigger?: Element | null) => {
    // Trigger = the element that opened the modal: a page link (light DOM) or
    // the banner's Customize button (shadow DOM). Focus goes back to it on close.
    returnFocus = trigger || (props.root.activeElement as Element) || null
    setModalSource(source)
    setModal(true)
  }
  props.registerOpen((source = 'settings_link', trigger) => openModal(source, trigger))

  /** Return focus to the trigger; if it is gone (banner Customize unmounted) use the banner's Customize button, then the page body. */
  const restoreFocus = () => {
    const el = returnFocus as HTMLElement | null
    returnFocus = null
    if (el && el.isConnected && typeof el.focus === 'function') {
      // icon-only triggers (e.g. the sidebar <i> menu button) are not natively focusable
      if (!el.matches('a[href],button,input,select,textarea,summary,[tabindex]')) el.setAttribute('tabindex', '-1')
      return el.focus()
    }
    const customize = props.root.querySelector?.('[data-testid="consent-customize"]') as HTMLElement | null
    if (customize) return customize.focus()
  }

  const closeModal = () => {
    setModal(false)
    // wait for Solid to re-render the banner (if it comes back) before focusing
    queueMicrotask(restoreFocus)
  }

  /** Run a save; on success hide the banner/modal and show COPY.saved, on failure keep them open and show COPY.saveError. */
  const run = (save: () => unknown): boolean => {
    setBusy(true)
    setSaveError(false)
    setNotice(false)
    try {
      save()
    } catch {
      // storage write failed (ConsentWriteError) or anything unexpected:
      // keep the banner/modal open so the visitor can try again
      setSaveError(true)
      return false
    } finally {
      setBusy(false)
    }
    setBanner(false)
    if (modal()) closeModal()
    setNotice(true)
    clearTimeout(timer)
    timer = setTimeout(() => setNotice(false), 4000)
    return true
  }

  const commit = (c: { analytics: boolean; marketing: boolean }, action: 'accept_all' | 'reject_all' | 'customize', source: ConsentSurface) =>
    run(() => saveConsent(c, action, source, { post: props.onSave }))

  /** CCPA "Do Not Sell or Share": marketing off (GPC withdrawal path), analytics unchanged. */
  const doNotSell = (source: ConsentSurface = 'settings_link') => run(() => optOutOfSale(source, { post: props.onSave }))
  props.registerDoNotSell?.(doNotSell)
  onCleanup(() => clearTimeout(timer))

  const initial = () => {
    const s = getConsent()
    return s ? { analytics: s.choices.analytics, marketing: s.choices.marketing } : { analytics: false, marketing: false }
  }

  return (
    <div class="tc-root">
      <Show when={banner() && !modal()}>
        <ConsentBanner
          gpc={gpc}
          busy={busy()}
          privacyUrl={getConfig().privacyUrl}
          onReject={() => commit({ analytics: false, marketing: false }, 'reject_all', 'banner')}
          onCustomize={(e?: Event) => openModal('preferences_modal', (e?.currentTarget as Element) || null)}
          onAccept={() => commit({ analytics: true, marketing: true }, 'accept_all', 'banner')}
          onDoNotSell={() => doNotSell('banner')}
        />
      </Show>
      <Show when={modal()}>
        <ConsentPreferencesModal
          initial={initial()}
          gpc={gpc}
          busy={busy()}
          root={props.root}
          onClose={closeModal}
          onReject={() => commit({ analytics: false, marketing: false }, 'reject_all', modalSource())}
          onSave={(c) => commit(c, 'customize', modalSource())}
          onAccept={() => commit({ analytics: true, marketing: true }, 'accept_all', modalSource())}
        />
      </Show>
      {/* The only live region: always in the DOM so screen readers pick up the text change. */}
      <div role="status" aria-live="polite" aria-atomic="true" data-testid="consent-status" class="tc-bottom fixed left-1/2 -translate-x-1/2">
        <Show when={notice()}>
          <span
            data-testid="consent-saved"
            class="block rounded-lg border border-[color:var(--tc-border)] bg-[var(--tc-surface-overlay)] px-4 py-2 text-[13px] text-[color:var(--tc-success)]"
          >
            {COPY.saved}
          </span>
        </Show>
        <Show when={saveError()}>
          <span
            data-testid="consent-save-error"
            class="block rounded-lg border border-[color:var(--tc-border)] bg-[var(--tc-surface-overlay)] px-4 py-2 text-[13px] text-[color:var(--tc-error,#ff688a)]"
          >
            {COPY.saveError}
          </span>
        </Show>
      </div>
    </div>
  )
}
